import InfoIcon from '@mui/icons-material/Info'
import { Box, Stack } from '@mui/material'
import { FormProvider } from 'react-hook-form'
import {
    QualificationRefIds,
    getGetIdsUsersCurrentQueryKey,
    getGetIdsUsersMyAccountHealthcareserviceHcsIdQueryKey,
    usePutIdsUsersCurrent,
} from '@toktokdoctech/tokapi-client'
import { displaySnackbar } from '../../actions/main'
import useCurrentHcs from '../../hooks/useCurrentHcs'
import { useAppDispatch } from '../../store'
import { tanstackQueryClient } from '../../api/tanstackClient'
import CommonLoaderBackdrop from '../Common/CommonLoaderBackdrop'
import FormContainer from '../Common/form/formContainer/FormContainer'
import FormSection from '../Common/form/formSection/FormSection'
import SaveComponent from '../Common/SaveComponent/SaveComponent'
import SearchRpps from '../Common/searchRpps/SearchRpps'
import ActionDialog from '../feature/actionDialog/ActionDialog'
import { UserData } from './_props'
import sxs from './_sxs'
import { ContactPhone } from './fields/ContactPhone'
import { Email } from './fields/Email'
import MentionsAndTitles from './fields/MentionAndTitles'
import PrivatePhone from './fields/PrivatePhone'
import SecuredEmail from './fields/SecuredEmail'
import UserFunction from './fields/UserFunction'
import IdentitySection from './sections/IdentitySection'
import ProfessionalSection from './sections/ProfessionalSection'
import { buildCommonFields } from './utils/addOrUpdateUser/updatePayloadBuilders'
import useUserFormBase from './utils/useUserFormBase'

type MyAccountFormProps = {
    data: UserData
}

export const MyAccountForm = ({ data }: MyAccountFormProps) => {
    const dispatch = useAppDispatch()
    const { id: hcsId } = useCurrentHcs()

    const putCurrentUser = usePutIdsUsersCurrent()

    const {
        form,
        state,
        update,
        certifiedAccessMode,
        isLoading,
        hasFormError,
        globalErrorMessage,
        setGlobaleErrorMessage,
        clearGlobaleErrorMessage,
        userRpps,
        searchRppsHasError,
        handleRppsChange,
        cancelButton,
        redirectButton,
        buildHandleSaveTrigger,
        buildHandleSave,
        buildHandleCancel,
        buildSaveButtonAction,
    } = useUserFormBase({ mode: 'EDITION', data, returnLocation: '/' })

    const save = async (userData: UserData) => {
        update({ isLoading: true, loaderMessage: 'Enregistrement des modifications' })
        try {
            const { identity, rppsNum, professions, qualification, speciality, speMentions } =
                buildCommonFields(userData)

            const payload = {
                ...identity,
                rppsNum: rppsNum ?? null,
                professions,
                qualification: qualification as QualificationRefIds,
                speciality,
                speMentions: speMentions.length > 0 ? speMentions : undefined,
                contactPhone: userData.contactPhone ?? '',
            }

            await putCurrentUser.mutateAsync({ data: payload })

            tanstackQueryClient.invalidateQueries({
                queryKey: getGetIdsUsersMyAccountHealthcareserviceHcsIdQueryKey(hcsId),
            })
            tanstackQueryClient.invalidateQueries({
                queryKey: getGetIdsUsersCurrentQueryKey(),
            })
            update({ isLoading: false, loaderMessage: undefined })
            dispatch(displaySnackbar('success', 'Les modifications ont bien été enregistrées !'))
            return true
        } catch (err) {
            console.error({ err })

            if (err instanceof Error) {
                clearGlobaleErrorMessage()
                dispatch(displaySnackbar('error', err.message))
            }

            if (typeof err === 'string') {
                setGlobaleErrorMessage(err)
                dispatch(displaySnackbar('error', err))
            }

            update({ isLoading: false, loaderMessage: undefined })
            return undefined
        }
    }

    const handleSave = buildHandleSave(save)
    const handleSaveTrigger = buildHandleSaveTrigger(handleSave)
    const handleCancel = buildHandleCancel()

    return (
        <>
            <FormProvider {...form}>
                {!data.rpps && (
                    <FormContainer sx={{ ...sxs.UserForm, marginY: '2.125rem' }}>
                        <SearchRpps
                            title="Me rechercher dans l'annuaire santé"
                            displayTitle
                            onRppsUserChange={handleRppsChange}
                            selectedValue={undefined}
                            codeprofession={undefined}
                            required={certifiedAccessMode}
                            error={searchRppsHasError && !userRpps}
                            errorMessage="Cette information est obligatoire et n'a pas été renseignée"
                        />
                    </FormContainer>
                )}
                <Box sx={sxs.UserFormContainer}>
                    <FormContainer sx={sxs.UserForm}>
                        <IdentitySection />
                        <ProfessionalSection>
                            <MentionsAndTitles />
                            <UserFunction />
                        </ProfessionalSection>
                        <FormSection label="Coordonnées de contact">
                            <Email />
                            <SecuredEmail readonly={false} />
                            <PrivatePhone />
                            <ContactPhone />
                        </FormSection>

                        <Stack alignItems="center" gap="1.5rem" sx={{ width: '100%' }}>
                            <SaveComponent
                                withMargin={false}
                                saveButton={{ action: buildSaveButtonAction(handleSaveTrigger), isLoading }}
                                cancelButton={{ action: handleCancel, displayed: true }}
                                disableAll={isLoading}
                                shouldBlockNavigation={state.shouldBlockNavigation}
                                errorMessage={
                                    hasFormError || Boolean(globalErrorMessage) ? globalErrorMessage : undefined
                                }
                            />
                        </Stack>
                    </FormContainer>
                </Box>
            </FormProvider>
            {isLoading && <CommonLoaderBackdrop open label={state.loaderMessage} />}
            <ActionDialog
                open={Boolean(state.redirectKey)}
                onClose={() => update({ redirectKey: undefined })}
                title={`Vous allez être redirigé vers l'interface de changement de ${
                    state?.redirectKey === 'email' ? 'courriel de contact' : 'numéro de téléphone'
                }`}
                content="En cas d'autres modifications apportées à votre compte, elles ne seront pas sauvegardées : assurez-vous de les enregistrer avant de poursuivre."
                cancelProps={cancelButton}
                saveProps={redirectButton}
                icon={<InfoIcon />}
            />
        </>
    )
}
